import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const LiveStatsSection = () => {
  const [counts, setCounts] = useState({
    students: 0,
    courses: 0,
    instructors: 0,
    companies: 0
  });

  const finalCounts = {
    students: 15847,
    courses: 450,
    instructors: 125,
    companies: 850
  };

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setCounts({
        students: Math.floor(finalCounts.students * progress),
        courses: Math.floor(finalCounts.courses * progress),
        instructors: Math.floor(finalCounts.instructors * progress),
        companies: Math.floor(finalCounts.companies * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts(finalCounts);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      label: "Active Webinars",
      value: counts.students.toLocaleString(),
      icon: "Video",
      color: "from-blue-500 to-blue-600",
      percentage: 95
    },
    {
      label: "Total Attendees",
      value: counts.courses.toLocaleString() + "K+",
      icon: "Users",
      color: "from-indigo-500 to-indigo-600",
      percentage: 88
    },
    {
      label: "Expert Hosts",
      value: counts.instructors.toLocaleString(),
      icon: "Award",
      color: "from-blue-600 to-indigo-700",
      percentage: 92
    },
    {
      label: "Partner Organizations",
      value: counts.companies.toLocaleString(),
      icon: "Building2",
      color: "from-indigo-600 to-blue-700",
      percentage: 97
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800 relative overflow-hidden">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 bg-green-400 rounded-full"
            />
            <span className="text-sm font-bold text-white">Live Platform Metrics</span>
          </div>
          
          <h2 className="text-5xl font-black text-white mb-4">
            Real-Time Impact
          </h2>
          <p className="text-xl text-blue-200">
            Join thousands of professionals transforming virtual events
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="relative group"
            >
              {/* Glow Effect */}
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(59, 130, 246, 0.3)',
                    '0 0 40px rgba(59, 130, 246, 0.6)',
                    '0 0 20px rgba(59, 130, 246, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition-opacity"
              />

              {/* Card */}
              <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20">
                {/* Icon */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mb-6 shadow-xl`}
                >
                  <Icon name={stat.icon} size={32} className="text-white" />
                </motion.div>

                {/* Number */}
                <motion.div
                  className="text-5xl font-black text-white mb-2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  {stat.value}
                </motion.div>

                {/* Label */}
                <div className="text-blue-200 text-sm font-semibold mb-4">
                  {stat.label}
                </div>

                {/* Progress Bar */}
                <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${stat.percentage}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: index * 0.2 }}
                    className={`h-full bg-gradient-to-r ${stat.color} rounded-full`}
                  />
                </div>

                {/* Percentage */}
                <div className="text-right text-xs text-blue-300 mt-2 font-semibold">
                  {stat.percentage}% Growth
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-16 text-center bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20"
        >
          <p className="text-white text-lg mb-4">
            <span className="font-bold">Over 2,500 sessions</span> hosted this month
          </p>
          <div className="flex items-center justify-center gap-8 text-sm text-blue-200">
            <div className="flex items-center gap-2">
              <Icon name="TrendingUp" size={16} />
              <span>+45% Growth</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Globe" size={16} />
              <span>200+ Countries</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={16} />
              <span>99.9% Uptime</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStatsSection;
